/** SA-110: manifest-driven parameter editors — the Phase 4 write-through
 * surface. `editable` up front, `advanced` in a local fold-out (deliberately
 * NOT the ConfigureBar global advanced switch, which gates structural /
 * destructive affordances), `locked` read-only (the server's
 * overlay_parameter_locked finding is the backstop for hand-authored
 * drafts/imports/packs). Every write goes through the store's pruning
 * setParameter, so values stay in sync with the bespoke section editors by
 * construction. */
import { useState, type ReactNode } from 'react'
import { Lock } from 'lucide-react'
import { CollapsibleSection } from '@/components/ui/CollapsibleSection'
import { TagInput } from '@/components/ui/TagInput'
import { useWorkflowConfigStore } from '@/stores/workflowConfigStore'
import { BilingualTextEditor } from './editors'
import { InlineFindings } from './entryControls'
import { formatParamValue } from './formatParamValue'
import type {
  WorkflowManifest,
  WorkflowManifestParameter,
  WorkflowManifestParameterSchema,
} from '@/types/workflowConfig'

const inputCls =
  'mt-1 w-full rounded-sm border border-border bg-background p-2 text-xs text-foreground'

function overlayValueAt(overlay: unknown, path: string): unknown {
  let node: unknown = overlay
  for (const part of path.split('.')) {
    if (!node || typeof node !== 'object' || !(part in (node as Record<string, unknown>))) {
      return undefined
    }
    node = (node as Record<string, unknown>)[part]
  }
  return node
}

function paramLabel(key: string, param: WorkflowManifestParameter): string {
  return param.label?.en ?? key
}

/** Bounded numeric commit: refuse blank/NaN/non-integer/out-of-bounds instead
 * of writing garbage into the overlay (the handoffEditors commitPositiveInt
 * posture, generalized to declared minimum/maximum). `Number(raw)` — NOT
 * `parseInt`/`parseFloat` — is load-bearing: those truncate a trailing
 * fraction or trailing garbage (`parseInt('6.5', 10) === 6`,
 * `parseInt('6abc', 10) === 6`) before the integer check ever runs, so the
 * check becomes dead code. `Number('6.5')` stays 6.5 (fails Number.isInteger)
 * and `Number('6abc')` is NaN. */
function commitBoundedNumber(
  raw: string,
  schema: WorkflowManifestParameterSchema,
): number | null {
  if (raw.trim() === '') return null
  const n = Number(raw)
  if (Number.isNaN(n)) return null
  if (schema.type === 'integer' && !Number.isInteger(n)) return null
  if (schema.minimum != null && n < schema.minimum) return null
  if (schema.maximum != null && n > schema.maximum) return null
  return n
}

function ParameterField({ paramKey, param, languages }: {
  paramKey: string
  param: WorkflowManifestParameter
  languages: string[]
}) {
  const overlay = useWorkflowConfigStore((s) => s.overlay)
  const setParameter = useWorkflowConfigStore((s) => s.setParameter)
  const overlayValue = overlayValueAt(overlay, param.path)
  const effective = overlayValue !== undefined ? overlayValue : param.default
  const [buffer, setBuffer] = useState<string | null>(null)
  const schema = param.schema

  let control: ReactNode
  if (schema.type === 'localized_text') {
    const value = (overlayValue ?? {}) as Record<string, string>
    const baseValue = (param.default ?? {}) as Record<string, string>
    control = (
      <BilingualTextEditor
        value={value}
        baseValue={baseValue}
        languages={languages}
        onChange={(lang, text) =>
          setParameter(`${param.path}.${lang}`, text, baseValue[lang])
        }
      />
    )
  } else if (schema.type === 'localized_string_list') {
    const value = (overlayValue ?? {}) as Record<string, string[]>
    const baseValue = (param.default ?? {}) as Record<string, string[]>
    const langs = Array.from(
      new Set([...languages, ...Object.keys(baseValue), ...Object.keys(value)]),
    )
    control = (
      <div className="grid gap-2 sm:grid-cols-2">
        {langs.map((lang) => (
          <div key={lang} className="text-xs">
            <span className="font-mono uppercase text-muted-foreground">{lang}</span>
            <TagInput
              value={value[lang] ?? baseValue[lang] ?? []}
              onChange={(list) => setParameter(`${param.path}.${lang}`, list, baseValue[lang])}
              normalize={(s) => s.trim()}
              maxTags={50}
              ariaLabel={`parameter ${paramKey} phrases in ${lang}`}
              placeholder="Add a phrase and press Enter"
              className="mt-1"
            />
          </div>
        ))}
      </div>
    )
  } else if (schema.type === 'boolean') {
    control = (
      <input
        type="checkbox"
        aria-label={`parameter ${paramKey}`}
        checked={Boolean(effective)}
        onChange={(e) => setParameter(param.path, e.target.checked, param.default)}
      />
    )
  } else if (schema.type === 'enum') {
    const unset = effective === null || effective === undefined
    control = (
      <select
        aria-label={`parameter ${paramKey}`}
        className={inputCls}
        value={unset ? '' : String(effective)}
        onChange={(e) => {
          if (e.target.value !== '') setParameter(param.path, e.target.value, param.default)
        }}
      >
        {unset && <option value="">— not set —</option>}
        {(schema.values ?? []).map((v) => (
          <option key={v} value={v}>{v}</option>
        ))}
      </select>
    )
  } else if (schema.type === 'integer' || schema.type === 'number') {
    control = (
      <input
        type="number"
        aria-label={`parameter ${paramKey}`}
        className={inputCls}
        min={schema.minimum ?? undefined}
        max={schema.maximum ?? undefined}
        value={buffer ?? String(effective ?? '')}
        onChange={(e) => setBuffer(e.target.value)}
        onBlur={() => {
          if (buffer === null) return
          const n = commitBoundedNumber(buffer, schema)
          if (n !== null) setParameter(param.path, n, param.default)
          setBuffer(null)
        }}
      />
    )
  } else if (schema.type === 'string_list') {
    const value = (overlayValue ?? param.default ?? []) as string[]
    control = (
      <TagInput
        value={value}
        onChange={(list) => setParameter(param.path, list, param.default)}
        normalize={(s) => s.trim()}
        maxTags={50}
        ariaLabel={`parameter ${paramKey}`}
        placeholder="Add an entry and press Enter"
      />
    )
  } else {
    control = (
      <input
        aria-label={`parameter ${paramKey}`}
        className={inputCls}
        value={buffer ?? String(effective ?? '')}
        onChange={(e) => setBuffer(e.target.value)}
        onBlur={() => {
          if (buffer === null) return
          setParameter(param.path, buffer, param.default)
          setBuffer(null)
        }}
      />
    )
  }

  return (
    <div className="space-y-1">
      <p className="text-xs font-medium text-foreground">
        {paramLabel(paramKey, param)}
      </p>
      {param.help?.en && (
        <p className="text-[11px] text-muted-foreground">{param.help.en}</p>
      )}
      {control}
      {(param.default !== null && param.default !== undefined) || overlayValue !== undefined ? (
        <p className="flex items-center gap-2 text-[11px] text-muted-foreground">
          <span>Default: {formatParamValue(param.default, schema, languages)}</span>
          {overlayValue !== undefined && (
            <button
              type="button"
              className="underline decoration-dotted hover:text-foreground"
              onClick={() => setParameter(param.path, param.default, param.default)}
            >
              Reset to default
            </button>
          )}
        </p>
      ) : null}
      <InlineFindings paths={[param.path]} />
    </div>
  )
}

function LockedRow({ paramKey, param, languages }: {
  paramKey: string
  param: WorkflowManifestParameter
  languages: string[]
}) {
  return (
    <div className="space-y-0.5">
      <p className="flex items-center gap-1 text-xs font-medium text-foreground">
        <Lock aria-hidden className="h-3 w-3" />
        {paramLabel(paramKey, param)}
      </p>
      <p className="text-[11px] text-muted-foreground">
        Locked by the template — {formatParamValue(param.default, param.schema, languages)}
      </p>
      <InlineFindings paths={[param.path]} />
    </div>
  )
}

export function ParametersCard({ manifest, languages }: {
  manifest: WorkflowManifest | null
  languages: string[]
}) {
  const entries = Object.entries(manifest?.parameters ?? {})
  if (entries.length === 0) return null
  const editable = entries.filter(([, p]) => p.visibility === 'editable')
  const advanced = entries.filter(([, p]) => p.visibility === 'advanced')
  const locked = entries.filter(([, p]) => p.visibility === 'locked')
  return (
    <CollapsibleSection
      title="Template parameters"
      variant="soft"
      storageKey="wf-section:parameters"
      subtitle="Curated knobs this template declares"
    >
      <div className="space-y-4">
        {editable.map(([key, param]) => (
          <ParameterField key={key} paramKey={key} param={param} languages={languages} />
        ))}
        {advanced.length > 0 && (
          <CollapsibleSection
            title="Advanced parameters"
            variant="soft"
            defaultOpen={false}
            storageKey="wf-section:params-advanced"
            summary={`${advanced.length} advanced`}
          >
            <div className="space-y-4">
              {advanced.map(([key, param]) => (
                <ParameterField key={key} paramKey={key} param={param} languages={languages} />
              ))}
            </div>
          </CollapsibleSection>
        )}
        {locked.map(([key, param]) => (
          <LockedRow key={key} paramKey={key} param={param} languages={languages} />
        ))}
      </div>
    </CollapsibleSection>
  )
}
